import React, { useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Badge } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BugReportIcon from '@mui/icons-material/BugReport';
import OperationFilterPanel from './OperationFilterPanel';
import { classificationApi, envisFindingsApi } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const OperationFilterManager = ({
  // Props from parent (MapViewPage)
  onFilterChange, // Callback to pass selected filters up
  operationDefects, // Current defects/findings data
  displayedCount, // Current number of displayed defects
}) => {
  const theme = useTheme();
  const { user, isAuthenticated, getUserOrganization } = useAuth();

  // State variables
  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  
  // Filter options data
  const [filterOptions, setFilterOptions] = useState({
    defectTypes: [],
    devices: [],
    networks: [],
    sections: [],
    regions: []
  });

  // Current filter values
  const [filters, setFilters] = useState({
    defectTypeId: '',
    deviceId: '',
    networkId: '',
    sectionId: '',
    region: '',
    validationStatus: '',
    actionStatus: '',
    dateRange: 'today',
    customDateFrom: '',
    customDateTo: '',
    searchTerm: ''
  });

  // Handlers
  const toggleFilterPanel = () => {
    setIsFilterPanelVisible(prev => !prev);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...filters,
      [filterType]: value
    };
    setFilters(newFilters);
    
    // Pass filters to parent
    if (typeof onFilterChange === 'function') {
      onFilterChange(newFilters);
    }
  };

  const handleResetFilters = () => {
    const resetFilters = {
      defectTypeId: '',
      deviceId: '',
      networkId: '',
      sectionId: '',
      region: '',
      validationStatus: '',
      actionStatus: '',
      dateRange: 'today',
      customDateFrom: '',
      customDateTo: '',
      searchTerm: ''
    };
    setFilters(resetFilters);
    
    // Pass reset filters to parent
    if (typeof onFilterChange === 'function') {
      onFilterChange(resetFilters);
    }
  };

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      console.log('OperationFilterManager: useEffect triggered');
      console.log('OperationFilterManager: isAuthenticated:', isAuthenticated);
      console.log('OperationFilterManager: user:', user);
      
      const userOrganization = getUserOrganization();
      console.log('OperationFilterManager: userOrganization:', userOrganization);
      
      if (!isAuthenticated) {
        console.log('OperationFilterManager: User not authenticated');
        return;
      }
      
      if (!userOrganization?.id) {
        console.log('OperationFilterManager: No organization ID available');
        return;
      }

      console.log('OperationFilterManager: Fetching filter options for organization:', userOrganization);

      try {
        console.log('OperationFilterManager: Fetching defect types...');
        // Fetch defect types
        const defectTypesResponse = await classificationApi.getDefectTypes({
          organization_id: userOrganization.id,
          asset_type_id: 1 // Road asset type
        });
        console.log('OperationFilterManager: Defect types response:', defectTypesResponse);

        console.log('OperationFilterManager: Fetching findings data...');
        // Fetch devices (from findings data) - use higher limit to get all filter options
        const devicesResponse = await envisFindingsApi.getTableFindings({
          limit: 10000, // Increased limit to get comprehensive filter options
          offset: 0,
          organization_id: userOrganization.id
        });
        console.log('OperationFilterManager: Findings response:', devicesResponse);

        // Extract unique devices from findings
        const uniqueDevices = [];
        const deviceMap = new Map();
        
        devicesResponse.data.data?.forEach(finding => {
          if (finding.device_name && !deviceMap.has(finding.device_name)) {
            deviceMap.set(finding.device_name, {
              id: finding.device_uuid || finding.device_name,
              name: finding.device_name,
              uuid: finding.device_uuid
            });
            uniqueDevices.push(deviceMap.get(finding.device_name));
          }
        });

        // Extract unique networks and regions
        const uniqueNetworks = [];
        const uniqueRegions = [];
        const networkMap = new Map();
        const regionSet = new Set();

        devicesResponse.data.data?.forEach(finding => {
          if (finding.network_name && !networkMap.has(finding.network_name)) {
            networkMap.set(finding.network_name, {
              id: finding.network_id || finding.network_name,
              name: finding.network_name
            });
            uniqueNetworks.push(networkMap.get(finding.network_name));
          }

          if (finding.region && !regionSet.has(finding.region)) {
            regionSet.add(finding.region);
            uniqueRegions.push(finding.region);
          }
        });

        // Extract unique sections
        const uniqueSections = [];
        const sectionMap = new Map();

        devicesResponse.data.data?.forEach(finding => {
          if (finding.section_name && !sectionMap.has(finding.section_name)) {
            sectionMap.set(finding.section_name, {
              id: finding.section_id || finding.section_name,
              name: finding.section_name
            });
            uniqueSections.push(sectionMap.get(finding.section_name));
          }
        });

        const filterOptions = {
          defectTypes: defectTypesResponse.data || [],
          devices: uniqueDevices,
          networks: uniqueNetworks,
          sections: uniqueSections,
          regions: uniqueRegions.sort()
        };

        console.log('OperationFilterManager: Final filter options:', filterOptions);
        setFilterOptions(filterOptions);

      } catch (error) {
        console.error('OperationFilterManager: Error fetching filter options:', error);
        console.error('OperationFilterManager: Error details:', error.response || error.message);
        
        // Set some fallback data for testing
        console.log('OperationFilterManager: Setting fallback data for testing');
        setFilterOptions({
          defectTypes: [
            { id: 1, name: 'Pothole', code: 'POT' },
            { id: 2, name: 'Crack', code: 'CRK' },
            { id: 3, name: 'Spalling', code: 'SPL' }
          ],
          devices: [
            { id: 'device-1', name: 'Device 001', uuid: 'device-1' },
            { id: 'device-2', name: 'Device 002', uuid: 'device-2' }
          ],
          networks: [
            { id: 1, name: 'Highway Network 1' },
            { id: 2, name: 'Highway Network 2' }
          ],
          sections: [
            { id: 1, name: 'Section A' },
            { id: 2, name: 'Section B' }
          ],
          regions: ['Central', 'Northern', 'Southern']
        });
      }
    };

    fetchFilterOptions();
  }, [getUserOrganization]);

  // Check if any filters are active for badge display
  const hasActiveFilters = Object.values(filters).some(value => 
    value !== '' && value !== 'today'
  );

  // Calculate counts for display
  const validatedCount = operationDefects.filter(d => d.isValidatedDefect).length;
  const pendingCount = operationDefects.filter(d => !d.isValidatedDefect && d.validationStatus !== 'rejected').length;
  const rejectedCount = operationDefects.filter(d => d.validationStatus === 'rejected').length;

  return (
    <>
      {/* Defect Filter Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 10,
        }}
      >
        <Tooltip
          title={isFilterPanelVisible ? "Hide Defect Filters" : "Show Defect Filters"}
          placement="right"
        >
          <Badge
            variant="dot"
            color="error"
            invisible={!hasActiveFilters}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                top: 6,
                right: 6,
              }
            }}
          >
            <IconButton
              onClick={toggleFilterPanel}
              size="large"
              sx={{
                backgroundColor: isFilterPanelVisible
                  ? alpha(theme.palette.primary.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
                color: isFilterPanelVisible ? 'white' : 'inherit',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isFilterPanelVisible
                    ? alpha(theme.palette.primary.main, 1)
                    : alpha(theme.palette.background.paper, 0.95),
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
              }}
            >
              <BugReportIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Badge>
        </Tooltip>
      </Box>

      {/* Render the Operation Filter Panel - only when visible */}
      {isFilterPanelVisible && (
        <OperationFilterPanel
          // Filter options data
          defectTypes={filterOptions.defectTypes}
          devices={filterOptions.devices}
          networks={filterOptions.networks}
          sections={filterOptions.sections}
          regions={filterOptions.regions}
          // Currently selected filter values
          selectedDefectTypeId={filters.defectTypeId}
          selectedDeviceId={filters.deviceId}
          selectedNetworkId={filters.networkId}
          selectedSectionId={filters.sectionId}
          selectedRegion={filters.region}
          selectedValidationStatus={filters.validationStatus}
          selectedActionStatus={filters.actionStatus}
          selectedDateRange={filters.dateRange}
          customDateFrom={filters.customDateFrom}
          customDateTo={filters.customDateTo}
          // Callback functions
          onDefectTypeChange={(value) => handleFilterChange('defectTypeId', value)}
          onDeviceChange={(value) => handleFilterChange('deviceId', value)}
          onNetworkChange={(value) => handleFilterChange('networkId', value)}
          onSectionChange={(value) => handleFilterChange('sectionId', value)}
          onRegionChange={(value) => handleFilterChange('region', value)}
          onValidationStatusChange={(value) => handleFilterChange('validationStatus', value)}
          onActionStatusChange={(value) => handleFilterChange('actionStatus', value)}
          onDateRangeChange={(value) => handleFilterChange('dateRange', value)}
          onCustomDateFromChange={(value) => handleFilterChange('customDateFrom', value)}
          onCustomDateToChange={(value) => handleFilterChange('customDateTo', value)}
          onResetFilters={handleResetFilters}
          // Counts for display
          displayedCount={displayedCount}
          totalCount={operationDefects.length}
          validatedCount={validatedCount}
          pendingCount={pendingCount}
          rejectedCount={rejectedCount}
          // Search term props
          searchTerm={filters.searchTerm}
          onSearchTermChange={(value) => handleFilterChange('searchTerm', value)}
        />
      )}
    </>
  );
};

export default OperationFilterManager; 